import { action, computed, observable, values, makeObservable } from 'mobx';

import { ProviderUid } from '@gem/call/CallProviders/callProviders.types';
import { Call } from '@gem/call/CallStore/Call';
import { MeOpponent } from '@gem/call/CallStore/Opponent/MeOpponent';
import { ClientRole } from '@gem/call/constants';

import { Opponent } from '../Opponent/Opponent';

export class Opponents {
  @observable meOpponent: MeOpponent | null = null;
  @observable private data = observable.map<ProviderUid, Opponent>({}, { deep: false });

  constructor(call: Call) {
    this.meOpponent = new MeOpponent({ call, uid: '' });
    makeObservable(this);
  }

  @computed
  get opponentList(): readonly Opponent[] {
    if (this.meOpponent) {
      return [...values(this.data), this.meOpponent];
    }

    return values(this.data);
  }

  @computed
  get opponentsWithoutScreenSharing(): readonly Opponent[] {
    return this.opponentList.filter((item) => !item.isSharedScreen);
  }

  @computed
  get opponentsPublishers(): readonly Opponent[] {
    return values(this.data).filter((item) => item.hasVideo || item.hasAudio);
  }

  @computed
  get opponentsWithVideo(): Opponent[] {
    return this.opponentList.filter((opponent) => opponent.hasVideo);
  }

  @computed
  get hasOpponents(): boolean {
    return this.opponentList.length > 0;
  }

  @action
  addOpponent = (opponentId: ProviderUid, opponent: Opponent): void => {
    this.data.set(opponentId, opponent);
  };

  @action
  deleteOpponent = (opponentId: ProviderUid): void => {
    const opponent = this.data.get(opponentId);

    if (opponent) {
      this.data.delete(opponentId);
    }
  };

  @action
  setOpponentRole = (opponentId: ProviderUid, role: ClientRole): void => {
    const opponent = this.data.get(opponentId);
    if (opponent) {
      opponent.setRole(role);
    }
  };

  @computed
  get screenSharingOpponent(): Opponent | null {
    return this.opponentList.find((opponent) => opponent.isSharedScreen) || null;
  }

  getOpponent = (uid: ProviderUid): Opponent | null => {
    if (this.meOpponent && this.meOpponent.uid === uid) {
      return this.meOpponent;
    }
    return this.data.get(uid) || null;
  };
}
